import type { ApiClient } from '../client';
import type {
  ApiResponse,
  ClassAttendanceSheetResponse,
  CreateClassDiaryRequest,
  CreateClassDiaryResponse,
  CreateLessonPlanRequest,
  CreateLessonPlanResponse,
  ListAttendanceAuditsResponse,
  ListClassDiaryResponse,
  ListLessonPlansResponse,
  ListTeacherClassesResponse,
  ListTeacherExamSubjectsResponse,
  ListTeacherExamsResponse,
  ListTeacherReportCardsResponse,
  QuickAttendanceRequest,
  QuickAttendanceResponse,
  QuickMarksRequest,
  QuickMarksResponse,
  TeacherMarksClassSheetResponse,
  TeachingEffectivenessResponse,
  Uuid,
} from '../types';

export function bindTeacherPortal(client: ApiClient) {
  return {
async listLessonPlans(params: {
    class_id: Uuid;
    subject_id: Uuid;
    from?: string;
    to?: string;
  }): Promise<ApiResponse<ListLessonPlansResponse>> {
    const q = new URLSearchParams({
      class_id: params.class_id,
      subject_id: params.subject_id,
    });
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    return client.request<ListLessonPlansResponse>(`/teacher-portal/lesson-plans?${q}`);
  },

async createLessonPlan(body: CreateLessonPlanRequest): Promise<ApiResponse<CreateLessonPlanResponse>> {
    return client.request<CreateLessonPlanResponse>('/teacher-portal/lesson-plans', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listClassDiary(params: {
    class_id: Uuid;
    from?: string;
    to?: string;
  }): Promise<ApiResponse<ListClassDiaryResponse>> {
    const q = new URLSearchParams({ class_id: params.class_id });
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    return client.request<ListClassDiaryResponse>(`/teacher-portal/class-diary?${q}`);
  },

async createClassDiary(body: CreateClassDiaryRequest): Promise<ApiResponse<CreateClassDiaryResponse>> {
    return client.request<CreateClassDiaryResponse>('/teacher-portal/class-diary', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async quickAttendanceEntry(body: QuickAttendanceRequest): Promise<ApiResponse<QuickAttendanceResponse>> {
    return client.request<QuickAttendanceResponse>('/teacher-portal/attendance/quick-entry', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async getClassAttendanceSheet(params: {
  class_id: Uuid;
  date: string;
  section_id?: Uuid;
  page?: number;
  limit?: number;
}): Promise<ApiResponse<ClassAttendanceSheetResponse>> {
  const qs = new URLSearchParams({
    class_id: params.class_id,
    date: params.date,
  });
  if (params.section_id) qs.set('section_id', params.section_id);
  if (params.page != null) qs.set('page', String(params.page));
  if (params.limit != null) qs.set('limit', String(params.limit));
  return client.request<ClassAttendanceSheetResponse>(
    `/teacher-portal/attendance/class-sheet?${qs.toString()}`
  );
},

async listAttendanceAudits(params?: {
  class_id?: string;
  date?: string;
  student_id?: string;
  actor_user_id?: string;
  page?: number;
  limit?: number;
}): Promise<ApiResponse<ListAttendanceAuditsResponse>> {
  const qs = new URLSearchParams();
  if (params?.class_id?.trim()) qs.set('class_id', params.class_id.trim());
  if (params?.date?.trim()) qs.set('date', params.date.trim());
  if (params?.student_id?.trim()) qs.set('student_id', params.student_id.trim());
  if (params?.actor_user_id?.trim()) qs.set('actor_user_id', params.actor_user_id.trim());
  if (params?.page != null) qs.set('page', String(params.page));
  if (params?.limit != null) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return client.request<ListAttendanceAuditsResponse>(
    query
      ? `/teacher-portal/attendance/audits?${query}`
      : '/teacher-portal/attendance/audits'
  );
},

async quickMarksEntry(body: QuickMarksRequest): Promise<ApiResponse<QuickMarksResponse>> {
    return client.request<QuickMarksResponse>('/teacher-portal/marks/quick-entry', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listTeacherMarksClasses(): Promise<ApiResponse<ListTeacherClassesResponse>> {
    return client.request<ListTeacherClassesResponse>('/teacher-portal/marks/classes');
  },

async listTeacherMarksExams(): Promise<ApiResponse<ListTeacherExamsResponse>> {
    return client.request<ListTeacherExamsResponse>('/teacher-portal/marks/exams');
  },

async listTeacherMarksExamSubjects(params: {
    class_id: Uuid;
    exam_id: Uuid;
  }): Promise<ApiResponse<ListTeacherExamSubjectsResponse>> {
    const qs = new URLSearchParams({
      class_id: params.class_id,
      exam_id: params.exam_id,
    });
    return client.request<ListTeacherExamSubjectsResponse>(
      `/teacher-portal/marks/exam-subjects?${qs.toString()}`
    );
  },

async getTeacherMarksClassSheet(params: {
    class_id: Uuid;
    exam_subject_id: Uuid;
    section_id?: Uuid;
  }): Promise<ApiResponse<TeacherMarksClassSheetResponse>> {
    const qs = new URLSearchParams({
      class_id: params.class_id,
      exam_subject_id: params.exam_subject_id,
    });
    if (params.section_id) qs.set('section_id', params.section_id);
    return client.request<TeacherMarksClassSheetResponse>(
      `/teacher-portal/marks/class-sheet?${qs.toString()}`
    );
  },

async listTeacherReportCards(params: {
    class_id: Uuid;
    exam_id?: Uuid;
  }): Promise<ApiResponse<ListTeacherReportCardsResponse>> {
    const qs = new URLSearchParams({ class_id: params.class_id });
    if (params.exam_id) qs.set('exam_id', params.exam_id);
    return client.request<ListTeacherReportCardsResponse>(
      `/teacher-portal/marks/report-cards?${qs.toString()}`
    );
  },

  /**
   * Teacher: effectiveness for own teaching assignments only (session-scoped).
   * Never returns other teachers’ aggregates.
   */
  async getMyTeachingEffectiveness(params?: {
    academic_year?: string;
    term?: string;
  }): Promise<ApiResponse<TeachingEffectivenessResponse>> {
    const qs = new URLSearchParams();
    if (params?.academic_year?.trim()) qs.set('academic_year', params.academic_year.trim());
    if (params?.term?.trim()) qs.set('term', params.term.trim());
    const query = qs.toString();
    return client.request<TeachingEffectivenessResponse>(
      query
        ? `/teacher-portal/analytics/teaching-effectiveness?${query}`
        : '/teacher-portal/analytics/teaching-effectiveness'
    );
  },
  };
}
